import { useState } from 'react';
import { useAuth } from '@/App';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Star, MessageSquare, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export function ReviewsSection({
  reviews,
  reviewAverages,
  reviewTotal,
  onWriteReview,
  onSubmitAdminResponse,
}) {
  const { isAuthenticated, isAdmin } = useAuth();
  const { t } = useI18n();
  const [respondingTo, setRespondingTo] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');

  const handleSubmitResponse = (reviewId) => {
    if (!adminResponse.trim()) return;
    onSubmitAdminResponse(reviewId, adminResponse);
    setRespondingTo(null);
    setAdminResponse('');
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>
            {t('detail.reviews')}
          </h3>
          {reviewAverages && (
            <div className="flex items-center gap-1.5 bg-[#F5E4DE] px-3 py-1 rounded-full">
              <Star className="w-4 h-4 text-[#C36F4D] fill-[#C36F4D]" />
              <span className="font-semibold text-[#C36F4D]">{reviewAverages.avg_overall?.toFixed(1)}</span>
              <span className="text-sm text-[#78716C]">({reviewTotal})</span>
            </div>
          )}
        </div>
        {isAuthenticated && (
          <Button
            onClick={onWriteReview}
            className="bg-[#264639] hover:bg-[#1C352B]"
            data-testid="write-review-btn"
          >
            <Star className="w-4 h-4 mr-2" /> {t('detail.writeReview')}
          </Button>
        )}
      </div>

      {/* Category Averages */}
      {reviewAverages && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6 p-4 bg-[#F5F4F0] rounded-xl">
          {[
            { key: 'avg_cleanliness', label: t('review.cleanliness') },
            { key: 'avg_location', label: t('review.location') },
            { key: 'avg_service', label: t('review.service') },
            { key: 'avg_value', label: t('review.value') },
            { key: 'avg_amenities', label: t('review.amenities') }
          ].map(({ key, label }) => (
            <div key={key} className="text-center">
              <p className="text-xs text-[#78716C] mb-1">{label}</p>
              <div className="flex items-center justify-center gap-1">
                <Star className="w-3.5 h-3.5 text-[#C36F4D] fill-[#C36F4D]" />
                <span className="font-semibold text-[#1C1917]">{reviewAverages[key]?.toFixed(1) || '-'}</span>
              </div>
              <div className="h-1.5 bg-[#E7E5E4] rounded-full mt-1.5 overflow-hidden">
                <div
                  className="h-full bg-[#C36F4D] rounded-full transition-all"
                  style={{ width: `${((reviewAverages[key] || 0) / 5) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-[#78716C]">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>Aún no hay reseñas para esta propiedad</p>
          {isAuthenticated && <p className="text-sm mt-1">¡Sé el primero en dejar una!</p>}
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.review_id} className="border-b border-[#E7E5E4] pb-6 last:border-0 last:pb-0" data-testid={`review-${review.review_id}`}>
              <div className="flex items-start gap-3 mb-3">
                {review.user_picture ? (
                  <img src={review.user_picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#DCE9E2] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#264639] font-semibold text-sm">{review.user_name?.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#1C1917]">{review.user_name}</span>
                    {review.source === 'airbnb' && (
                      <span className="text-[10px] bg-[#FF5A5F] text-white px-1.5 py-0.5 rounded font-medium">Airbnb</span>
                    )}
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <Star key={s} className={`w-3.5 h-3.5 ${s <= review.overall_rating ? 'text-[#C36F4D] fill-[#C36F4D]' : 'text-[#E7E5E4]'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-[#78716C]">
                    {review.created_at ? new Date(review.created_at).toLocaleDateString('es-PY', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                  </p>
                </div>
              </div>

              <p className="text-[#57534E] leading-relaxed mb-2">{review.comment}</p>

              {review.photos && review.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid={`review-photos-${review.review_id}`}>
                  {review.photos.map((photo) => (
                    <img
                      key={photo}
                      src={photo.startsWith('/api') ? `${API}${photo}` : photo}
                      alt=""
                      className="w-24 h-24 rounded-lg object-cover border border-[#E7E5E4] cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(photo.startsWith('/api') ? `${API}${photo}` : photo, '_blank', 'noopener,noreferrer')}
                    />
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-3 text-xs text-[#78716C]">
                {[
                  { k: 'cleanliness', l: t('review.cleanliness') },
                  { k: 'location', l: t('review.location') },
                  { k: 'service', l: t('review.service') },
                  { k: 'value', l: t('review.value') },
                  { k: 'amenities', l: t('review.amenities') }
                ].map(({ k, l }) => (
                  <span key={k} className="bg-[#F5F4F0] px-2 py-1 rounded">
                    {l}: {review.categories?.[k]}/5
                  </span>
                ))}
              </div>

              {/* Admin Response */}
              {review.admin_response && (
                <div className="mt-4 ml-6 p-4 bg-[#DCE9E2] rounded-lg border-l-4 border-[#264639]">
                  <p className="text-xs font-medium text-[#264639] mb-1">
                    {t('detail.ownerResponse')} {review.admin_response_by && `(${review.admin_response_by})`}
                  </p>
                  <p className="text-sm text-[#1C1917]">{review.admin_response}</p>
                </div>
              )}

              {isAdmin && !review.admin_response && (
                <div className="mt-3">
                  {respondingTo === review.review_id ? (
                    <div className="flex gap-2">
                      <Input
                        value={adminResponse}
                        onChange={(e) => setAdminResponse(e.target.value)}
                        placeholder="Escribe tu respuesta..."
                        className="flex-1 text-sm"
                        data-testid="admin-response-input"
                      />
                      <Button size="sm" onClick={() => handleSubmitResponse(review.review_id)} className="bg-[#264639] hover:bg-[#1C352B]">
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setRespondingTo(null); setAdminResponse(''); }}>
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-[#264639] text-xs" onClick={() => setRespondingTo(review.review_id)} data-testid="respond-review-btn">
                      <MessageSquare className="w-3.5 h-3.5 mr-1" /> Responder
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
